export const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type VisitKind = 'visitor' | 'review' | 'admin';
export interface Attribution {
  label: string | null;
  utm: Partial<Record<(typeof utmKeys)[number], string>>;
  warning: string;
  review: boolean;
  disabled: boolean;
}

export function validLinkLabel(value: string): boolean {
  return /^[a-z0-9][a-z0-9._-]{0,79}$/i.test(value);
}

// Store only documented, bounded campaign fields, never arbitrary query strings.
export function parseAttribution(search: string): Attribution {
  const params = new URLSearchParams(search);
  const utm: Attribution['utm'] = {};
  let warning = '';
  for (const key of utmKeys) {
    const value = params.get(key);
    if (value && validLinkLabel(value)) utm[key] = value.toLowerCase();
    else if (value) warning = 'Some invalid campaign fields were omitted.';
  }
  const reserved = new Set(['via', 'review', 'track', ...utmKeys]);
  const bare = [...params].filter(([key, value]) => !value && !reserved.has(key));
  const candidate = params.get('via') ?? (bare.length === 1 ? bare[0][0] : null);
  let label = null;
  if (candidate && validLinkLabel(candidate)) label = candidate.toLowerCase();
  else if (candidate || bare.length > 1 || params.has('via')) warning = 'Invalid or ambiguous link label; treated as unlabelled.';
  return { label, utm, warning, review: params.get('review') === '1', disabled: params.get('track') === 'off' };
}

export interface VisitSummary {
  opens: number;
  unlabelled: number;
  locked: number;
  reviews: number;
  admins: number;
  latestOpen: string | null;
}
export interface ProposalVisit {
  id: string;
  kind: VisitKind;
  label: string | null;
  utm: Attribution['utm'];
  warning: string;
  arrivedAt: string;
  openedAt: string | null;
}
export interface VisitReport {
  startedAt: string;
  legacyViews: number;
  summary: VisitSummary;
  visits: ProposalVisit[];
  total: number;
  page: number;
}

export function visitTime(value: string | null): string {
  return value ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' }).format(new Date(value)) : 'None recorded';
}
